import { GraphQLResult } from 'aws-amplify/api'
import type { Language } from '@/types'
import {
  NormalizedFieldOptions,
  normalizeFieldOptionsFromFieldItem,
} from './option.normalized'
import { toSupportedKey } from '@/services/property/property.adapter'
import { OptionItem, type SupportedKeys } from '@/services/property'
import { getFieldItem } from './field.query'
import { OptionFieldFilter } from '../queries/utils/filters'
import {
  optionDeletionWitnessChildrenQuery,
  optionDeletionWitnessesByFieldQuery,
  optionsByFieldQuery,
} from './field.graphql'
import { getAmplifyClient } from '@/services/amplifyClient'

/**
 * @deprecated Use {@link fetchAllOptionsByField} instead
 */
export const fetchFieldItemFn = async ({
  optionId,
  lang,
  optionFilter,
}: {
  optionId: string | undefined
  lang?: Language
  optionFilter: OptionFieldFilter | undefined
}): Promise<{
  normalized: NormalizedFieldOptions
  supportedKey: SupportedKeys
}> => {
  if (!optionId) {
    throw new Error('fetchFieldItemFn optionId is required')
  }
  const field = await getFieldItem({ id: optionId, lang, optionFilter })
  if (!field) {
    throw new Error(`fetchFieldItemFn Field ${optionId} not found`)
  }
  const normalized = normalizeFieldOptionsFromFieldItem(field)
  const supportedKey = toSupportedKey({ field, lang })

  return { normalized, supportedKey }
}

const normalizeOptionFilter = (optionFilter?: OptionFieldFilter) => {
  if (!optionFilter) return undefined
  return Object.keys(optionFilter).length > 0 ? optionFilter : undefined
}

const fetchOptionsByFieldFn = async ({
  fieldOptionsId,
  lang,
  optionFilter,
  limit,
  nextToken,
  childLimit,
}: {
  fieldOptionsId: string | undefined
  lang?: Language
  optionFilter?: OptionFieldFilter
  limit?: number
  nextToken?: string | null
  childLimit?: number
}) => {
  if (!fieldOptionsId) throw new Error('fieldOptionsId is required')
  const client = getAmplifyClient()
  const normalizedOptionFilter = normalizeOptionFilter(optionFilter)

  const { data } = (await client.graphql<{
    optionsByField: {
      items: OptionItem[]
      nextToken: string
    }
  }>({
    query: optionsByFieldQuery,
    variables: {
      fieldOptionsId,
      lang: lang ? { lang: { eq: lang } } : undefined,
      optionFilter: normalizedOptionFilter,
      limit,
      nextToken,
      childLimit,
    },
  })) as GraphQLResult<{
    optionsByField: { items: OptionItem[]; nextToken: string }
  }>

  return data.optionsByField
}

type DeletionWitness = { id: string; status?: string | null }
type WitnessPage = { items: DeletionWitness[]; nextToken?: string | null }

const withoutDeletedPayload = (
  items: OptionItem[],
  deletedOptionIds: ReadonlySet<string>,
): OptionItem[] =>
  items.flatMap((item) => {
    const children = item.options
      ? withoutDeletedPayload(item.options.items, deletedOptionIds)
      : undefined
    if (item.status === 'DELETED' || deletedOptionIds.has(item.id))
      return children ?? []
    return [{ ...item, ...(children ? { options: { items: children } } : {}) }]
  })

const fetchDeletionWitnesses = async (
  fieldOptionsId: string,
  optionFilter: OptionFieldFilter,
  lang?: Language,
) => {
  const client = getAmplifyClient()
  const witnesses = new Map<string, OptionItem>()
  const retainedChildren = new Map<string, OptionItem>()
  const collect = (items: DeletionWitness[]) => {
    for (const item of items) {
      if (item.status === 'DELETED') {
        // The empty label satisfies the existing cache shape; no deleted
        // payload other than identity/status enters the lookup cache.
        witnesses.set(item.id, {
          id: item.id,
          status: 'DELETED',
          label: '',
          value: '',
          metadata: [],
          userID: '',
          organizationID: '',
        })
      }
    }
  }
  let nextToken: string | null | undefined
  do {
    const { data } = (await client.graphql({
      query: optionDeletionWitnessesByFieldQuery,
      variables: {
        fieldOptionsId,
        lang: lang ? { lang: { eq: lang } } : undefined,
        parentFilter: { or: [optionFilter, { status: { eq: 'DELETED' } }] },
        optionFilter,
        limit: 500,
        childLimit: 500,
        nextToken,
      },
    })) as GraphQLResult<{
      optionsByField: {
        items: Array<
          DeletionWitness & {
            options?: { items: OptionItem[] } | null
            deletedOptions?: WitnessPage | null
          }
        >
        nextToken?: string | null
      }
    }>
    for (const parent of data!.optionsByField.items) {
      collect([parent])
      collect(parent.deletedOptions?.items ?? [])
      if (parent.status === 'DELETED') {
        for (const child of parent.options?.items ?? []) {
          if (child.status !== 'DELETED') retainedChildren.set(child.id, child)
        }
      }
      let childToken = parent.deletedOptions?.nextToken
      while (childToken) {
        const childResult = (await client.graphql({
          query: optionDeletionWitnessChildrenQuery,
          variables: { id: parent.id, nextToken: childToken, limit: 500 },
        })) as GraphQLResult<{ getOption: { options: WitnessPage } }>
        const page = childResult.data!.getOption.options
        collect(page.items)
        childToken = page.nextToken
      }
    }
    nextToken = data!.optionsByField.nextToken
  } while (nextToken)
  return {
    witnesses: [...witnesses.values()],
    retainedChildren: [...retainedChildren.values()],
  }
}

export const fetchAllOptionsByField = async ({
  fieldOptionsId,
  lang,
  optionFilter,
  includeDeletedWitnesses = false,
}: {
  fieldOptionsId: string | undefined
  lang?: Language
  optionFilter?: OptionFieldFilter
  includeDeletedWitnesses?: boolean
}) => {
  const normalizedOptionFilter = normalizeOptionFilter(optionFilter)
  if (includeDeletedWitnesses && !normalizedOptionFilter) {
    throw new Error('Consumer option scope is required for deletion witnesses')
  }
  const items: OptionItem[] = []
  let nextToken: string | null | undefined

  do {
    const response = await fetchOptionsByFieldFn({
      fieldOptionsId,
      lang,
      optionFilter,
      limit: 500,
      nextToken,
      childLimit: 500,
    })
    items.push(...response.items)
    nextToken = response.nextToken
  } while (nextToken)

  if (!includeDeletedWitnesses) return items
  const { witnesses, retainedChildren } = await fetchDeletionWitnesses(
    fieldOptionsId!,
    normalizedOptionFilter!,
    lang,
  )
  const deletedOptionIds = new Set(witnesses.map((item) => item.id))
  return [
    ...withoutDeletedPayload(items, deletedOptionIds),
    ...withoutDeletedPayload(retainedChildren, deletedOptionIds),
    ...witnesses,
  ]
}

// Read the current replacement identity directly, without the option lookup cache.
export const fetchOptionReplacementTarget = async (
  id: string,
  displayFilter?: OptionFieldFilter,
  nextToken?: string | null,
) => {
  const client = getAmplifyClient()
  const result = (await client.graphql({
    query: /* GraphQL */ `
      query GetOptionReplacementTarget(
        $id: ID!
        $displayFilter: ModelOptionFilterInput
        $nextToken: String
        $includeChildren: Boolean!
      ) {
        getOption(id: $id) {
          id
          status
          fieldOptionsId
          optionOptionsId
          options(filter: $displayFilter, limit: 100, nextToken: $nextToken)
            @include(if: $includeChildren) {
            items {
              id
            }
            nextToken
          }
        }
      }
    `,
    variables: {
      id,
      ...(displayFilter ? { displayFilter, nextToken } : {}),
      includeChildren: Boolean(displayFilter),
    },
  })) as GraphQLResult<{
    getOption: {
      id: string
      status?: string | null
      fieldOptionsId?: string | null
      optionOptionsId?: string | null
      options?: {
        items: Array<{ id: string }>
        nextToken?: string | null
      } | null
    } | null
  }>
  if (result.errors?.length)
    throw new Error('Replacement option could not be verified')
  return result.data?.getOption
}

// A replacement must be a leaf in the current display scope, including archived
// children. Legacy leaves may carry only their parent id; the parent's status
// does not determine the child's status.
export const isAcceptedReplacementInList = async (
  id: string,
  listId: string,
  displayFilter?: OptionFieldFilter,
) => {
  const filter = normalizeOptionFilter(displayFilter)
  if (!id || !listId || !filter) return false
  let target = await fetchOptionReplacementTarget(id, filter)
  const pageTokens = new Set<string>()
  while (true) {
    if (target?.id !== id || target.status !== 'ACCEPTED') return false
    const children = target.options
    if (!Array.isArray(children?.items) || children.items.length > 0)
      return false
    if (!children.nextToken) break
    if (pageTokens.has(children.nextToken)) return false
    pageTokens.add(children.nextToken)
    target = await fetchOptionReplacementTarget(id, filter, children.nextToken)
  }
  let current = target
  const visited = new Set([id])
  while (current) {
    if (current.fieldOptionsId != null) return current.fieldOptionsId === listId
    const parentId = current.optionOptionsId
    if (!parentId || visited.has(parentId)) return false
    visited.add(parentId)
    const parent = await fetchOptionReplacementTarget(parentId)
    if (parent?.id !== parentId) return false
    current = parent
  }
  return false
}
